import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../api';
import { Eye, EyeOff, LogIn, AlertCircle, Sun, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { login } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await authAPI.login(username, password);
            
            login({
                id: response.user_id,
                username: response.username,
                role: response.role,
                requires_setup: response.requires_setup,
                is_finance: response.is_finance || false
            }, response.token);

            // Redirect based on role and setup status
            if (response.role === 'user' && response.requires_setup) {
                navigate('/setup');
            } else if (response.role === 'super_admin') {
                navigate('/super-admin');
            } else if (response.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/user');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`login-page min-h-screen flex ${isDark ? 'bg-void-black' : 'bg-cream-light'}`}>
            {/* Left side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 login-brand-section relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-neon-green/20 to-transparent" />
                <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
                    <div className="text-center">
                        <h1 className="text-6xl font-headings font-bold text-white mb-4 tracking-tight">
                            VIRTUAL
                            <span className="block text-neon-green">GAMES</span>
                            <span className="block">WALLET</span>
                        </h1>
                        <p className="text-zinc-400 text-lg mt-6 max-w-md">
                            Kelola kredit game, transfer saldo, dan lacak riwayat transaksi dengan mudah.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right side - Login Form */}
            <div className={`w-full lg:w-1/2 flex items-center justify-center p-8 ${isDark ? 'bg-void-black' : 'bg-cream-light'}`}>
                <div className="w-full max-w-md">
                    {/* Theme Toggle */}
                    <div className="flex justify-end mb-8">
                        <button
                            onClick={toggleTheme}
                            className={`p-2 rounded-md ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-200'} transition-colors`}
                            aria-label="Toggle theme"
                        >
                            {isDark ? (
                                <Sun className="w-5 h-5 text-yellow-400" />
                            ) : (
                                <Moon className="w-5 h-5 text-zinc-600" />
                            )}
                        </button>
                    </div>

                    {/* Mobile Logo */}
                    <div className="lg:hidden text-center mb-8">
                        <h1 className={`text-3xl font-headings font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                            VIRTUAL <span className="text-neon-green">GAMES</span> WALLET
                        </h1>
                    </div>

                    {/* Login Form */}
                    <div className={`login-form animate-slide-up ${!isDark ? 'bg-white border-zinc-300' : ''}`}>
                        {/* Logo/Brand */}
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neon-green/10 flex items-center justify-center">
                                <span className="text-3xl">🎮</span>
                            </div>
                            <h2 className={`text-2xl font-headings font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                                Welcome Back
                            </h2>
                            <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>
                                Sign in to your account
                            </p>
                        </div>

                        {error && (
                            <div className="alert-error mb-6 animate-slide-up">
                                <AlertCircle className="w-4 h-4" />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="username" className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
                                    Username
                                </Label>
                                <Input
                                    id="username"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Enter your username"
                                    className={`h-12 ${isDark ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-zinc-300 text-black'}`}
                                    required
                                    data-testid="login-username"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
                                    Password
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password"
                                        className={`h-12 pr-12 ${isDark ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-zinc-300 text-black'}`}
                                        required
                                        data-testid="login-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-black'}`}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full h-12 bg-neon-green hover:bg-neon-green/90 text-void-black font-headings font-semibold text-lg"
                                data-testid="login-submit"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="spinner w-4 h-4" />
                                        Authenticating...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <LogIn className="w-4 h-4" />
                                        Sign In
                                    </span>
                                )}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
